// Modal that re-authenticates the current user (password + MFA) for a step-up challenge.
// Reuses the existing Cognito sign-in primitives; a successful re-auth refreshes the
// session's auth_time so the retried sensitive action passes the backend's freshness gate.

import { useState } from "react";

import {
  currentEmail,
  signIn as cognitoSignIn,
  submitTotpCode,
  totpErrorMessage,
  TotpRequiredError,
} from "./cognito";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function StepUpDialog({
  open,
  onDone,
}: {
  open: boolean;
  onDone: (ok: boolean) => void;
}) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [pendingUser, setPendingUser] = useState<TotpRequiredError["user"] | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPassword("");
    setCode("");
    setNeedsCode(false);
    setPendingUser(null);
    setStatus("");
    setBusy(false);
  };

  const submitPassword = async () => {
    setStatus("");
    setBusy(true);
    try {
      const email = await currentEmail();
      if (!email) {
        setStatus("Could not determine your account. Sign out and back in.");
        return;
      }
      await cognitoSignIn(email, password);
      // No MFA challenge (unexpected given mandatory MFA) but succeeded: done.
      reset();
      onDone(true);
    } catch (err) {
      if (err instanceof TotpRequiredError) {
        setPendingUser(err.user);
        setNeedsCode(true);
      } else {
        setStatus(err instanceof Error ? err.message : "Re-authentication failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    setStatus("");
    if (!/^\d{6}$/.test(code.trim())) {
      setStatus("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      await submitTotpCode(pendingUser!, code.trim());
      reset();
      onDone(true);
    } catch (err) {
      setStatus(totpErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    reset();
    onDone(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="step-up-reauth"
    >
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg">Confirm it's you</CardTitle>
          <CardDescription>
            Re-enter your password and authenticator code.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!needsCode ? (
            <div className="space-y-2">
              <Label htmlFor="stepup-password">Password</Label>
              <Input
                id="stepup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label="stepup-password"
                autoComplete="current-password"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="stepup-code">Authenticator code</Label>
              <Input
                id="stepup-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-label="stepup-code"
                placeholder="123456"
                autoComplete="one-time-code"
              />
            </div>
          )}

          {status && <p className="status text-sm text-destructive">{status}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={cancel} disabled={busy}>
              Cancel
            </Button>
            {!needsCode ? (
              <Button
                type="button"
                onClick={() => void submitPassword()}
                disabled={busy || !password}
              >
                {busy ? "Checking…" : "Continue"}
              </Button>
            ) : (
              <Button type="button" onClick={() => void submitCode()} disabled={busy}>
                {busy ? "Verifying…" : "Verify"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
